import { Data, Input, Image, Part, Deployment, Model, ModelVersion, RunnerSelector, UserAppIDSet } from 'api/grpc/resources';
import { PostModelOutputsRequest } from 'api/grpc/service';
import { JavaScriptValue } from 'google-protobuf/google/protobuf/struct_pb';
import { getModelPredictRequestBody } from 'modules/Models/components/ModelForm/utils/utils';
import { FilePreview } from 'modules/Playground/Components/ChatInput';
import { getMessageHistory, PlaygroundMessage } from 'modules/Playground/store/messageStore';
import { PlaygroundConfig } from 'modules/Playground/types/playgroundConfig';
import { V2 } from 'types/swagger/clarifai-api';
import { cleanEmptyFieldsInFieldMap } from './cleanEmptyFieldsInFieldMap';
import { constructPartsFromParams } from './constructPartsFromParams';

interface ConstructNewModelInterfaceRequestArgs {
  values: PlaygroundConfig;
  text: string;
  images: FilePreview[];
  currentUserId: string;
  userOrOrgId: string;
  appId: string;
  chatMessage?: PlaygroundMessage[];
}

export const constructNewModelInterfaceRequest = ({
  values,
  text,
  images,
  currentUserId,
  userOrOrgId,
  appId,
  chatMessage = [],
}: ConstructNewModelInterfaceRequestArgs): PostModelOutputsRequest => {
  const fieldValueMap = cleanEmptyFieldsInFieldMap(values.fieldValueMap);
  const requestBody = getModelPredictRequestBody(fieldValueMap) as V2.V2PostModelOutputs.RequestBody;

  const params = // Legacy models will have params in this field
    (requestBody?.model?.model_version?.output_info?.params ??
      // model inference will have params directly set in requestBody
      requestBody ??
      // Finally fallback to empty object
      {}) as Record<string, JavaScriptValue>;

  const request = new PostModelOutputsRequest();

  const messageHistory = chatMessage.length ? chatMessage : getMessageHistory();

  const requestData = new Data();
  const textPart = new Part();
  const textData = new Data();
  textData.setStringValue(text);
  textPart.setId('prompt');
  textPart.setData(textData);
  const partsList = [textPart];

  const partsFromParams = constructPartsFromParams(params, values.modelParamSpecs);
  partsList.push(...partsFromParams);

  if (images.length) {
    const imagePart = new Part();
    const imageData = new Data();
    const image = new Image();
    image.setBase64(images[0]?.base64);
    imageData.setImage(image);
    imagePart.setId('image');
    imagePart.setData(imageData);
    partsList.push(imagePart);
  }

  if (messageHistory.length > 1) {
    const historyList = messageHistory.slice(0, -1).map((message) => {
      return {
        role: message.role,
        content: [
          {
            type: 'text',
            text: message.content,
          },
          ...(message.file?.[0]?.base64
            ? [
                {
                  type: 'image_url',
                  image_url: {
                    url: `data:${message.file?.[0]?.file.type};base64,${message.file?.[0]?.base64}`,
                  },
                },
              ]
            : []),
        ],
      };
    });

    const historyParts = historyList.map((message) => {
      return new Part().setData(new Data().setStringValue(JSON.stringify(message)));
    });

    const historyKeyPart = new Part();
    const historyData = new Data();
    historyData.setPartsList(historyParts);
    historyKeyPart.setId('chat_history');
    historyKeyPart.setData(historyData);
    partsList.push(historyKeyPart);
  }

  const userAppIdSet = new UserAppIDSet();
  userAppIdSet.setUserId(values.model?.user_id ?? userOrOrgId);
  userAppIdSet.setAppId(values.model?.app_id ?? appId);
  request.setUserAppId(userAppIdSet);

  request.setModelId(values.model?.id ?? '');

  // model object
  const model = new Model();
  model.setId(values.model?.id ?? '');

  // model version
  const modelVersion = new ModelVersion();
  modelVersion.setId(values.modelVersion?.id ?? values.model?.model_version?.id ?? '');
  model.setModelVersion(modelVersion);

  // Runner selector
  const grpcRunner = new RunnerSelector();
  const grpcDeployment = new Deployment();
  grpcDeployment.setId(values.deploymentId ?? '');
  grpcDeployment.setUserId(currentUserId); // 👈 Important this needs to be current user, not userOrOrgId
  grpcRunner.setDeployment(grpcDeployment);
  if (values.deploymentId) {
    request.setRunnerSelector(grpcRunner);
  }

  // Inputs
  requestData.setPartsList(partsList);
  const input = new Input();
  input.setData(requestData);
  request.setInputsList([input]);
  request.setModel(model);

  return request;
};
